/**
 * Echo Hunt -- the live (DB-backed) corpus index pass (P5).
 *
 * Per the ground rules (§16.0), all the logic lives in the pure `Index.ts`; this thin `*_Live.ts`
 * wrapper is the only place that touches the journal DB and the `echoCorpusBullets` table.
 *
 * What "incremental" means here (§16.2 P5 acceptance):
 *  - a bullet whose `id` + `sourceTextHash` is ALREADY in `echoCorpusBullets` is skipped (re-running is
 *    a no-op);
 *  - a bullet whose id exists but whose hash changed gets re-indexed (editing one journal bullet
 *    re-indexes exactly its own dream, not the journal);
 *  - rows whose bullet no longer exists in the journal are marked stale rather than deleted, so a
 *    temporarily-blanked `publicText` does not destroy the index (it is exposed as `isStale`).
 *
 * Progress/cancel: `IndexCorpus_Live` takes a mutable `IndexRunControl`, which the Corpus tab owns
 * (so a re-render does not lose the cancel button). Cancelling stops BETWEEN dreams, so a partially
 * indexed run always leaves whole dreams indexed rather than half of one.
 */
import {MeID} from "../Server/DB/users.js";
import {GetJournalEntries} from "../Server/DB/journalEntries.js";
import {
	AddEchoCorpusBullet,
	DeleteEchoCorpusBullet,
	UpdateEchoCorpusBullet,
} from "../Server/Commands/EchoCorpusBulletCommands.js";
import {EchoCorpusBullet, GetEchoCorpusBullets} from "../Server/DB/echoCorpusBullets/@EchoCorpusBullet.js";
import {store} from "../Store/index.js";
import {LLMApi} from "../Store/main/settings.js";
import {ExtractBulletsDetailed, SourceBullet} from "./Bullets.js";
import {CheckBulletOptions} from "./Normalize.js";
import {liveLLMBackend} from "./LLM_Live.js";
import {LLMCallCounter, ResolveModelForRole} from "./LLM.js";
import {GetCheckOptionsFromProfile} from "./Bullets_Live.js";
import {GetMyEchoProfile} from "./Profiles_Live.js";
import {
	IndexDreamBullets,
	IndexSummary,
	IndexedBullet,
	PrepareBulletsForIndexing,
	SummarizeIndexedBullets,
} from "./CorpusIndex.js";

// control
// ==========

/** Mutable run state, owned by the caller so a component re-render can still cancel the run. */
export class IndexRunControl {
	cancelled = false;
	/** bullets written so far (reals + skipped) */
	done = 0;
	/** bullets that need a model call */
	total = 0;
	/** dream-level failures, surfaced in the Corpus tab */
	errors: string[] = [];
	finished = false;

	cancel() {
		this.cancelled = true;
	}
}

// report shapes
// ==========

export type CorpusIndexReport = {
	/** every extracted source bullet across the journal */
	sourceBullets: SourceBullet[];
	/** cached rows, keyed by bullet id */
	rowsById: Map<string, EchoCorpusBullet>;
	/** source bullets with no cached row yet (they need a model call) */
	missing: SourceBullet[];
	/** source bullets whose cached row has an outdated `sourceTextHash` (re-indexed) */
	changed: SourceBullet[];
	/** cached rows whose source bullet no longer exists (its dream's post was edited/removed) */
	staleRows: EchoCorpusBullet[];
	/** bullets whose row is up to date -- re-running the index pass would skip these */
	upToDate: number;
	summary: IndexSummary;
};

// reads
// ==========

/** The user's cached corpus rows. Reactive (a mobx-graphlink accessor). */
export function GetMyEchoCorpusBullets(): EchoCorpusBullet[] {
	return GetEchoCorpusBullets(MeID() ?? null);
}

/** Compares the journal against the cached corpus rows, without writing anything. This is what makes
 * the "re-running is a no-op" acceptance criterion visible: `missing` + `changed` are exactly the
 * bullets a run would pay a model for. */
export function GetCorpusIndexReport(): CorpusIndexReport {
	const userID = MeID();
	const entries = userID == null ? [] : GetJournalEntries(userID);
	const checkOptions = GetCheckOptionsFromProfile();

	const sourceBullets: SourceBullet[] = [];
	for (const entry of entries) {
		sourceBullets.push(...ExtractBulletsDetailed(entry).bullets);
	}

	const rows = GetMyEchoCorpusBullets();
	const rowsById = new Map<string, EchoCorpusBullet>();
	for (const row of rows) {
		if (row.bulletId != null) rowsById.set(row.bulletId, row);
	}

	const missing: SourceBullet[] = [];
	const changed: SourceBullet[] = [];
	let upToDate = 0;
	const liveBulletIds = new Set<string>();

	for (const bullet of sourceBullets) {
		liveBulletIds.add(bullet.id);
		const row = rowsById.get(bullet.id);
		if (row == null) {
			missing.push(bullet);
		} else if (row.sourceTextHash != bullet.sourceTextHash) {
			changed.push(bullet);
		} else {
			upToDate++;
		}
	}

	const staleRows = rows.filter(row=>row.bulletId != null && !liveBulletIds.has(row.bulletId));

	// the summary covers the CACHED rows (what the pipeline will actually sample from), not the raw
	// journal: a bullet that has not been indexed yet is not usable material
	const indexedBullets = rows.map(RowToIndexedBullet);

	return {
		sourceBullets,
		rowsById,
		missing,
		changed,
		staleRows,
		upToDate,
		summary: SummarizeIndexedBullets(indexedBullets),
	};
}

/** Rebuilds an `IndexedBullet` from a cached row, so `SummarizeIndexedBullets` (and later phases'
 * samplers) can work on DB rows without a second shape. */
export function RowToIndexedBullet(row: EchoCorpusBullet): IndexedBullet {
	return {
		bulletId: row.bulletId ?? row.id,
		selfContainedText: row.selfContainedText ?? "",
		eventType: row.eventType ?? "other",
		oddityCount: row.oddityCount ?? 0,
		filterReason: row.filterReason ?? null,
		postPassViolations: row.postPassViolations ?? null,
		attempts: 1,
	};
}

/** The per-role model for the indexer role, resolved against the user's settings (§16.1). */
export function ResolveIndexerModel(): string {
	const settings = store.main.settings;
	const globalModel = settings.llm_mainAPI == LLMApi.openRouter ? settings.openRouterModel : settings.anthropicModel;
	return ResolveModelForRole(GetMyEchoProfile()?.modelRoles, "indexer", globalModel);
}

// writes
// ==========

/** Runs the incremental index pass (P5).
 *
 * Only bullets in `missing`/`changed` cost a model call, and calls are made per DREAM (so references
 * resolve and so a cancel leaves whole dreams indexed). On completion, stale rows are deleted -- they
 * describe bullets that no longer exist, so keeping them would let the pipeline sample ghost material.
 *
 * `control` is mutated as the run progresses, so the caller's progress bar and cancel button work.
 *
 * `onlyDreamId` restricts the run to one dream (the Corpus tab's per-row "re-index dream" action).
 * It is deliberately *not* limited by idempotency: an explicit re-index of an up-to-date dream still
 * re-runs it, since the point of the button is "the model's verdict looks wrong, ask again". */
export async function IndexCorpus_Live(control: IndexRunControl, options?: {dreamLimit?: number, onlyDreamId?: string}): Promise<{dreamsIndexed: number, bulletsWritten: number, errors: string[]}> {
	const report = GetCorpusIndexReport();
	const onlyDreamId = options?.onlyDreamId;
	const pending = onlyDreamId != null
		? report.sourceBullets.filter(a=>a.dreamId == onlyDreamId)
		: [...report.missing, ...report.changed];

	control.done = report.upToDate;
	control.total = report.sourceBullets.length;
	control.errors = [];
	control.finished = false;

	if (control.total == 0) {
		control.finished = true;
		return {dreamsIndexed: 0, bulletsWritten: 0, errors: []};
	}

	const checkOptions: CheckBulletOptions = GetCheckOptionsFromProfile();
	const profile = GetMyEchoProfile();
	const counter = new LLMCallCounter();
	const model = ResolveIndexerModel();

	// group only the PENDING bullets by dream; a dream is indexed when any of its bullets changed, and
	// the whole dream is re-sent so cross-bullet references stay resolvable
	let dreamGroups = PrepareBulletsForIndexing(pending, checkOptions);
	if (options?.dreamLimit != null) dreamGroups = dreamGroups.slice(0, options.dreamLimit);

	let dreamsIndexed = 0;
	let bulletsWritten = 0;

	const pendingById = new Map(pending.map(a=>[a.id, a]));

	// loop the dreams ourselves (rather than calling `IndexDreams`) so a cancel takes effect BETWEEN
	// dreams -- a partially-indexed run then always leaves whole dreams indexed, never half of one
	for (const dreamGroup of dreamGroups) {
		if (control.cancelled) break;

		const result = await IndexDreamBullets(liveLLMBackend, dreamGroup, {
			bannedWords: profile?.bannedTerms ?? [],
			blacklist: (profile?.clicheBlacklist ?? []).map(a=>a.phrase),
			model,
			counter,
		});
		dreamsIndexed++;
		if (result.error != null) control.errors.push(`${result.dreamId}: ${result.error}`);

		// write one row per indexed bullet (add when new, update in place when it already exists)
		for (const indexed of result.bullets) {
			const sourceBullet = pendingById.get(indexed.bulletId);
			if (sourceBullet == null) continue;

			const existing = report.rowsById.get(indexed.bulletId);
			const fields = IndexedBulletToRowFields(indexed, sourceBullet);

			try {
				if (existing == null) {
					await new AddEchoCorpusBullet({entry: new EchoCorpusBullet(fields)}).RunOnServer();
				} else {
					await new UpdateEchoCorpusBullet({id: existing.id, updates: fields}).RunOnServer();
				}
				bulletsWritten++;
				control.done++;
			} catch (ex) {
				control.errors.push(`${indexed.bulletId}: failed to save (${ex})`);
			}
		}
	}

	// stale rows describe bullets that no longer exist; drop them so samplers never see ghost material
	// (skipped for a single-dream re-index, which must not touch other dreams' rows)
	if (!control.cancelled && onlyDreamId == null) {
		for (const row of report.staleRows) {
			try {
				await new DeleteEchoCorpusBullet({id: row.id}).RunOnServer();
			} catch (ex) {
				control.errors.push(`${row.bulletId ?? row.id}: failed to delete stale row (${ex})`);
			}
		}
	}

	control.finished = true;
	return {dreamsIndexed, bulletsWritten, errors: control.errors};
}

/** The DB row fields for one indexed bullet (everything except id/creator/createdAt, which the
 * commands set). */
function IndexedBulletToRowFields(indexed: IndexedBullet, sourceBullet: SourceBullet) {
	return {
		dreamId: sourceBullet.dreamId,
		sourceTextHash: sourceBullet.sourceTextHash,
		bulletId: sourceBullet.id,
		date: sourceBullet.date,
		heading: sourceBullet.heading,
		rawText: sourceBullet.rawText,
		selfContainedText: indexed.selfContainedText,
		eventType: indexed.eventType,
		oddityCount: indexed.oddityCount,
		filterReason: indexed.filterReason,
		postPassViolations: indexed.postPassViolations ?? null,
		indexedAt: Date.now(),
	};
}

/** Manually overrides one bullet's `filterReason` (§P5 "a manual override of `filterReason`").
 * `null` means "treat as usable". */
export async function SetCorpusBulletFilterReason_Live(rowID: string, reason: EchoCorpusBullet["filterReason"]): Promise<void> {
	await new UpdateEchoCorpusBullet({id: rowID, updates: {filterReason: reason}}).RunOnServer();
}
